// [cursor 2026-06-01] S87-T4 finish-up: durable Bookmarks mini-feed used in Mini
// RiskFlow, Bulletin, and Desk Rail. Reads from the backend service, shows an
// honest degraded/empty state, marks promoted Risk Signals, and gates the
// Ask Harper label to IV > 6.5 or promoted Risk Signal items.
import { Sparkles, Trash2 } from "lucide-react";
import type { BookmarkRecord } from "../../lib/services/bookmarks";
import backendClient from "../../lib/backend";
import { BOOKMARKS_CHANGED_EVENT } from "../../lib/bookmarks-bus";
import {
  DEFAULT_HEADLINE_PROMPT,
  dispatchAskHarperIntent,
} from "../../lib/chat-intents";
import { useBookmarks } from "./useBookmarks";

const ASK_HARPER_IV_THRESHOLD = 6.5;

function askHarperEligible(bookmark: BookmarkRecord): boolean {
  if (bookmark.riskSignalReviewStatus === "promoted") return true;
  if (bookmark.sourceType === "risk_signal") return true;
  return typeof bookmark.ivScore === "number"
    ? bookmark.ivScore > ASK_HARPER_IV_THRESHOLD
    : false;
}

function askHarper(bookmark: BookmarkRecord) {
  dispatchAskHarperIntent({
    prompt: DEFAULT_HEADLINE_PROMPT,
    context: {
      kind: bookmark.sourceType === "risk_signal" ? "risk_signal" : "headline",
      sourceType: bookmark.sourceType,
      sourceId: bookmark.sourceId,
      title: bookmark.title,
      summary: bookmark.summary,
      ivScore: bookmark.ivScore,
      imageUrl: bookmark.imageUrl,
      url: bookmark.url,
    },
  });
}

async function removeBookmark(id: string) {
  await backendClient.bookmarks.delete(id).catch(() => undefined);
  window.dispatchEvent(new CustomEvent(BOOKMARKS_CHANGED_EVENT));
}

export function BookmarksFeed({ limit = 20 }: { limit?: number }) {
  const { items, state } = useBookmarks({ limit });

  if (state === "loading") {
    return (
      <div className="px-3 py-6 text-center text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/40">
        Loading bookmarks…
      </div>
    );
  }

  if (state === "degraded") {
    return (
      <div className="mx-3 my-3 rounded-md border border-[var(--fintheon-accent)]/20 bg-[var(--fintheon-accent)]/5 px-3 py-2 text-[10px] uppercase tracking-[0.12em] text-[var(--fintheon-text)]/55">
        Bookmarks unavailable. Sign in or reconnect to view saved items.
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="px-3 py-6 text-center text-[11px] text-[var(--fintheon-text)]/45">
        No bookmarks yet. Use the ribbon on a card to save one.
      </div>
    );
  }

  return (
    <ul className="flex flex-col gap-1.5 px-2 py-2">
      {items.map((bookmark) => (
        <li
          key={bookmark.id}
          className="group rounded-[6px] border border-[var(--fintheon-accent)]/10 bg-[var(--fintheon-accent)]/[0.03] px-2.5 py-2"
        >
          <div className="flex items-start gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1.5">
                {bookmark.riskSignalReviewStatus === "promoted" && (
                  <span
                    title="Promoted to Risk Signal"
                    className="inline-flex items-center gap-0.5 text-[9px] uppercase tracking-[0.08em] text-[var(--fintheon-accent)]"
                  >
                    <Sparkles size={9} />
                    Risk Signal
                  </span>
                )}
                {typeof bookmark.ivScore === "number" && (
                  <span className="text-[9px] tabular-nums text-[var(--fintheon-text)]/45">
                    IV {bookmark.ivScore.toFixed(1)}
                  </span>
                )}
              </div>
              <p className="mt-0.5 line-clamp-2 text-[11.5px] leading-snug text-[var(--fintheon-text)]/85">
                {bookmark.title}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void removeBookmark(bookmark.id)}
              title="Remove bookmark"
              className="shrink-0 text-[var(--fintheon-text)]/30 hover:text-red-400"
            >
              <Trash2 size={12} />
            </button>
          </div>
          {askHarperEligible(bookmark) && (
            <button
              type="button"
              onClick={() => askHarper(bookmark)}
              className="mt-1 text-[10px] font-medium uppercase tracking-[0.1em] text-[var(--fintheon-accent)]/80 hover:text-[var(--fintheon-accent)]"
            >
              Ask Harper
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}
